import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Info } from "lucide-react";

export function AboutMedCarerDialog() {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <button className="text-primary hover:underline inline-flex items-center gap-1 text-sm">
          <Info className="h-4 w-4" />
          What is MedCarer?
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[85vh] p-0">
        <DialogHeader className="bg-primary px-6 py-5 rounded-t-lg">
          <DialogTitle className="text-primary-foreground text-xl font-bold">MedCarer</DialogTitle>
          <p className="text-primary-foreground/90 text-sm">Secure Medication Management for You and those you care for</p>
        </DialogHeader>
        <ScrollArea className="px-6 py-4 max-h-[65vh]">
          <div className="space-y-5 text-sm text-foreground">
            <section>
              <h3 className="font-semibold text-primary mb-1">What is MedCarer?</h3>
              <p className="text-muted-foreground leading-relaxed">
                MedCarer is a FREE secure web app that helps individuals, families, and parents manage medications, allergies, and childhood vaccinations in one place. Track what you take, share an emergency summary with health professionals, give trusted carers controlled access, and keep your kids' immunisation records organised against the Australian National Immunisation Program (NIP).
              </p>
            </section>

            <section>
              <h3 className="font-semibold text-primary mb-2">Who it's for</h3>
              <ul className="space-y-1.5 text-muted-foreground">
                <li className="flex gap-2"><span className="text-primary font-bold">•</span><span><strong>Patients</strong> managing their own medications and allergies</span></li>
                <li className="flex gap-2"><span className="text-primary font-bold">•</span><span><strong>Carers</strong> helping family members or clients (with the patient's permission)</span></li>
                <li className="flex gap-2"><span className="text-primary font-bold">•</span><span><strong>Parents</strong> tracking their children's vaccinations and allergies, with optional co-parent sharing</span></li>
              </ul>
            </section>

            <section>
              <h3 className="font-semibold text-primary mb-2">Key Features</h3>
              <ul className="space-y-1.5 text-muted-foreground">
                <li className="flex gap-2"><span className="text-primary font-bold">•</span>All medications and allergies in one secure place, with full change history</li>
                <li className="flex gap-2"><span className="text-primary font-bold">•</span>Emergency "Show to Doctor" view and shareable QR code for instant access by health professionals</li>
                <li className="flex gap-2"><span className="text-primary font-bold">•</span>Children's profiles with vaccination tracking against the Australian NIP schedule and allergy records</li>
                <li className="flex gap-2"><span className="text-primary font-bold">•</span>Co-parent invitations so both parents can view and update a child's record</li>
                <li className="flex gap-2"><span className="text-primary font-bold">•</span>Optional daily dose tracking (off by default) — turn it on in Settings if you'd like to log each dose</li>
                <li className="flex gap-2"><span className="text-primary font-bold">•</span>Add medications by scanning a barcode, photographing a label, or uploading a hospital discharge document</li>
                <li className="flex gap-2"><span className="text-primary font-bold">•</span>Email your medication & allergy list as a PDF or CSV to anyone who needs it</li>
                <li className="flex gap-2"><span className="text-primary font-bold">•</span>Invite trusted carers with controlled, revocable access</li>
                <li className="flex gap-2"><span className="text-primary font-bold">•</span>Install MedCarer to your home screen for quick app-like access on iPhone and Android</li>
              </ul>
            </section>

            <section>
              <h3 className="font-semibold text-primary mb-2">Security & Privacy</h3>
              <ul className="space-y-1.5 text-muted-foreground">
                <li className="flex gap-2"><span className="text-primary font-bold">•</span>Admin-approved accounts — every new user is verified before gaining access</li>
                <li className="flex gap-2"><span className="text-primary font-bold">•</span>Optional one-time passcode (OTP) and passkey sign-in for extra protection</li>
                <li className="flex gap-2"><span className="text-primary font-bold">•</span>Role-based access controls and row-level security on every record</li>
                <li className="flex gap-2"><span className="text-primary font-bold">•</span>Automatic logout after a period of inactivity</li>
                <li className="flex gap-2"><span className="text-primary font-bold">•</span>Inactive accounts are removed after 14 weeks of no activity</li>
                <li className="flex gap-2"><span className="text-primary font-bold">•</span>You control exactly who can see your information — revoke carer or co-parent access at any time</li>
              </ul>
            </section>

            <section>
              <h3 className="font-semibold text-primary mb-2">Getting Started</h3>
              <ol className="space-y-1 text-muted-foreground list-decimal list-inside">
                <li>Sign up with your name and email address</li>
                <li>Wait for admin approval (you'll receive an email)</li>
                <li>Add your medications, allergies, and any children you care for</li>
                <li>Invite a carer, co-parent, or share your emergency summary with your doctor</li>
              </ol>
            </section>

            <div className="bg-accent rounded-lg p-3 text-xs text-accent-foreground">
              <p><strong>Tip:</strong> On iPhone, open MedCarer in Safari, tap the Share button, then "Add to Home Screen". On Android, tap the browser menu and select "Install app" or "Add to Home Screen".</p>
            </div>

            <p className="text-xs text-muted-foreground text-center pb-2">
              Read our full Terms & Conditions after signing up
            </p>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
